// src/components/AuthForm.jsx
import { useRef, useState } from 'react';
import { TextField, IconButton, InputAdornment, CircularProgress } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { AuthenticationDetails, CognitoUser } from 'amazon-cognito-identity-js';

import { useCognitoContext } from '../../context/CognitoContext.jsx';
import { useAuthContext } from '../../context/AuthContext.jsx';
import { useSnackbar } from '../../context/SnackbarContext.jsx';
import { authenticateServer } from '../../utils/authUtils';
import { wakeDisplay } from '../../utils/displayUtils';
import { useTranslation } from '../../context/TranslationContext.jsx';
import { StyledCloseIcon } from '../../constants/styledComponents';
import {
  validateEmail,
  validatePassword,
  checkPasswordConditions,
} from '../../utils/authFormValidation';
import { TestWorkflowAction } from '../../constants/testWorkflowState';
import { useAppWorkflowContext } from '../../context/AppWorkflowContext.jsx';
import { useAppConfig } from '../../context/AppConfigContext.jsx';
import logger from '../../utils/logger';
import logError from '../../utils/logError';

import PasswordConditionsList from './PasswordConditionList.jsx';
import {
  AuthContainer,
  AuthFormGroup,
  AuthFieldControl,
  AuthFormHeader,
  AuthFormTitle,
  AuthFormSubtitle,
  AuthPasswordConditions,
  AuthForgotPasswordLinkContainer,
  AuthNavigationLink,
  AuthTopControls,
  AuthCloseButton,
  AuthFormControl,
  AuthFormContent,
  AuthErrorAlert,
  AuthSubmitButton,
} from './styles.js';

const getTranslationKey = (key) => key;
const translatedError = (key, params = {}, prefix = '', suffix = '') => ({
  key,
  params,
  prefix,
  suffix,
});
const literalError = (message) => ({ message });

const AuthForm = () => {
  const { translate } = useTranslation();

  const usernameInputProps = {
    type: 'email',
    name: 'username',
    autoComplete: 'username',
    placeholder: translate('authForm.fieldPlaceholders.email'),
  };
  const passwordInputProps = {
    name: 'password',
    autoComplete: 'current-password',
    placeholder: translate('authForm.fieldLabels.passwordLabel'),
  };
  const newPasswordInputProps = {
    name: 'new-password',
    autoComplete: 'new-password',
    placeholder: translate('authForm.fieldLabels.newPassword'),
  };
  const confirmPasswordInputProps = {
    name: 'confirm-password',
    autoComplete: 'new-password',
    placeholder: translate('authForm.fieldLabels.confirmPassword'),
  };
  const verificationCodeInputProps = {
    type: 'text',
    name: 'verification-code',
    autoComplete: 'one-time-code',
    inputMode: 'numeric',
    placeholder: translate('authForm.fieldLabels.verificationCode'),
  };

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [newPasswordError, setNewPasswordError] = useState('');
  const [passwordConfirmError, setPasswordConfirmError] = useState('');
  const [verificationCodeError, setVerificationCodeError] = useState('');
  const [userDetails, setUserDetails] = useState(null);
  const [state, setState] = useState(STATE_LOGIN);
  const [errorMessage, setErrorMessage] = useState('');
  const { openSnackbar } = useSnackbar();
  const { setUserData, setIsAuthenticated } = useAuthContext();
  const { userPool, signOut } = useCognitoContext();
  const [isPasswordFocused, setPasswordFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionLockRef = useRef(false);
  const { api, appName } = useAppConfig();

  const renderError = (error) => {
    if (!error) return '';
    if (typeof error === 'string') return translate(error);
    if (error.message) return error.message;

    return [error.prefix, translate(error.key, error.params), error.suffix]
      .filter(Boolean)
      .join(' ');
  };

  const passwordVisibilityAdornment = (visible, toggle) => ({
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          type="button"
          aria-label={
            visible
              ? translate('authForm.accessibility.hidePassword')
              : translate('authForm.accessibility.showPassword')
          }
          onClick={() => toggle((current) => !current)}
          onMouseDown={(event) => event.preventDefault()}
          onMouseUp={(event) => event.preventDefault()}
          edge="end"
          size="small"
        >
          {visible ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
        </IconButton>
      </InputAdornment>
    ),
  });

  const conditions = checkPasswordConditions(password);
  const { dispatchWorkflow } = useAppWorkflowContext();

  const startSubmitting = () => {
    if (submissionLockRef.current) return false;

    submissionLockRef.current = true;
    setIsSubmitting(true);
    return true;
  };

  const finishSubmitting = () => {
    submissionLockRef.current = false;
    setIsSubmitting(false);
  };

  const runCognitoRequest = (action, request) => {
    if (!startSubmitting()) return;

    try {
      request();
    } catch (error) {
      finishSubmitting();
      logger.error(logError(error, { action }));
      setErrorMessage(translatedError('authForm.generalError'));
    }
  };

  const flushPasswords = () => {
    setPasswordConfirm('');
    setPassword('');
    setShowPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const clearFieldErrors = () => {
    setUsernameError('');
    setPasswordError('');
    setNewPasswordError('');
    setPasswordConfirmError('');
    setVerificationCodeError('');
    setErrorMessage('');
  };

  const switchState = (nextState) => {
    clearFieldErrors();
    setState(nextState);
  };

  const usernameErr = validateEmail(username, getTranslationKey);
  const passwordErr = validatePassword(password, state, getTranslationKey);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (submissionLockRef.current) return;
    setErrorMessage('');
    const normalizedUsername = username.trim().toLowerCase();

    setUsernameError(usernameErr);
    setPasswordError(passwordErr);
    if (state === STATE_FORGOT_PASSWORD && usernameErr) {
      return;
    }

    if (state === STATE_FORGOT_PASSWORD_CHANGE) {
      setNewPasswordError(passwordErr);
    }

    switch (state) {
      case STATE_LOGIN:
        if (usernameErr || passwordErr) {
          return;
        }
        runCognitoRequest('cognitoAuthenticateRequest', () => {
          const authDetails = new AuthenticationDetails({
            Username: normalizedUsername,
            Password: password,
          });
          const cognitoUser = new CognitoUser({ Username: normalizedUsername, Pool: userPool });
          cognitoUser.authenticateUser(authDetails, {
            async onSuccess(result) {
              if (!result) {
                finishSubmitting();
                return;
              }

              try {
                const data = await authenticateServer(
                  result.getIdToken().getJwtToken(),
                  api.auth.baseUrl,
                );
                flushPasswords();
                setIsAuthenticated(true);
                // Fire-and-forget: must stay off the login critical path, so this is
                // deliberately not awaited. wakeDisplay() never throws. Guarded the
                // same way as App.jsx's wakeDisplayOnActivity call: an empty baseUrl
                // means acquisition isn't configured, and displayUtils.js would
                // otherwise build a bare "/display/wake" that fetch() resolves
                // against the frontend's own origin instead of skipping the call.
                if (api.acquire.baseUrl) {
                  wakeDisplay(api.acquire.baseUrl);
                }
                localStorage.setItem('userEmail', normalizedUsername);
                localStorage.setItem('isBackendAuthenticated', 'true');
                setUserData((prevData) => ({
                  ...prevData,
                  ...data,
                  email: normalizedUsername,
                }));
                dispatchWorkflow({ type: TestWorkflowAction.ENTER_APP_INIT });
              } catch (error) {
                if (error.response) {
                  const statusCode = error.response.status;
                  const serverError = error.response.data?.message;

                  switch (statusCode) {
                    case 400:
                      setErrorMessage(translatedError('authForm.badRequest'));
                      break;
                    case 401:
                      setErrorMessage(translatedError('authForm.unauthorized'));
                      break;
                    case 404:
                      setErrorMessage(translatedError('authForm.notFound', { appName }));
                      break;
                    case 503:
                      setErrorMessage(
                        serverError
                          ? literalError(serverError)
                          : translatedError('authForm.generalErrorWithStatus', { statusCode }),
                      );
                      break;
                    default:
                      setErrorMessage(
                        translatedError('authForm.generalErrorWithStatus', { statusCode }),
                      );
                  }
                } else if (error.request) {
                  logger.error(logError(error, { action: 'authNoResponse' }));
                  setErrorMessage(translatedError('authForm.noResponse', { appName }));
                } else {
                  logger.error(logError(error, { action: 'authRequestError' }));
                  setErrorMessage(translatedError('authForm.requestError', {}, '', error.message));
                }

                signOut();
              } finally {
                finishSubmitting();
              }
            },
            onFailure(err) {
              finishSubmitting();
              logger.error(logError(err, { action: 'cognitoAuthenticateFailure' }));
              if (err.code === 'UserNotFoundException' || err.code === 'NotAuthorizedException') {
                setErrorMessage(translatedError('authForm.invalidCredentials'));
              } else if (err.code === 'PasswordResetRequiredException') {
                setErrorMessage(translatedError('authForm.passwordResetRequired'));
              } else if (err.code === 'UserNotConfirmedException') {
                setErrorMessage(translatedError('authForm.userNotConfirmed'));
              } else if (
                err.code === 'LimitExceededException' ||
                err.code === 'TooManyRequestsException'
              ) {
                setErrorMessage(translatedError('authForm.tooManyAttempts'));
              } else {
                setErrorMessage(translatedError('authForm.generalError'));
              }
            },
            newPasswordRequired(userAttributes) {
              finishSubmitting();
              delete userAttributes.email_verified;
              delete userAttributes.email;
              setUserDetails({ user: cognitoUser, userAttr: userAttributes });
              flushPasswords();
              switchState(STATE_PASSWORD_CHANGE);
            },
          });
        });
        break;
      case STATE_FORGOT_PASSWORD:
        runCognitoRequest('cognitoForgotPasswordRequest', () => {
          const cognitoUser = new CognitoUser({ Username: normalizedUsername, Pool: userPool });
          cognitoUser.forgotPassword({
            onSuccess() {
              finishSubmitting();
              flushPasswords();
              switchState(STATE_FORGOT_PASSWORD_CHANGE);
            },
            onFailure(err) {
              finishSubmitting();
              logger.error(logError(err, { action: 'cognitoForgotPasswordFailure' }));
              setUsernameError(
                err.code === 'LimitExceededException'
                  ? 'authForm.attemptLimitExceeded'
                  : 'authForm.failedToInitiatePasswordReset',
              );
            },
          });
        });
        break;
      case STATE_FORGOT_PASSWORD_CHANGE:
        if (!verificationCode) {
          setVerificationCodeError('authForm.verificationCodeRequired');
          return;
        }
        setVerificationCodeError('');
        if (password === passwordConfirm) {
          if (passwordErr) {
            return;
          }
          runCognitoRequest('cognitoConfirmPasswordRequest', () => {
            const cognitoUser = new CognitoUser({ Username: normalizedUsername, Pool: userPool });
            cognitoUser.confirmPassword(verificationCode, password, {
              onSuccess() {
                finishSubmitting();
                flushPasswords();
                switchState(STATE_LOGIN);
                openSnackbar('success', translate('authForm.passwordChangedSuccessfully'));
              },
              onFailure(err) {
                finishSubmitting();
                logger.error(logError(err, { action: 'cognitoConfirmPasswordFailure' }));
                if (err.code === 'CodeMismatchException') {
                  setVerificationCodeError('authForm.invalidVerificationCode');
                } else if (err.code === 'LimitExceededException') {
                  setVerificationCodeError('authForm.tooManyAttempts');
                } else {
                  setVerificationCodeError('authForm.failedToVerifyCode');
                }
              },
            });
          });
        } else {
          setPasswordConfirmError('authForm.confirmPasswordError');
        }
        break;
      case STATE_PASSWORD_CHANGE:
        if (password === passwordConfirm) {
          if (passwordErr) {
            return;
          }
          runCognitoRequest('cognitoNewPasswordChallengeRequest', () => {
            userDetails.user.completeNewPasswordChallenge(password, userDetails.userAttr, {
              onSuccess(result) {
                finishSubmitting();
                if (result) {
                  flushPasswords();
                  switchState(STATE_LOGIN);
                }
              },
              onFailure(err) {
                finishSubmitting();
                logger.error(logError(err, { action: 'cognitoNewPasswordChallengeFailure' }));
                setErrorMessage(translatedError('authForm.generalError'));
              },
            });
          });
        } else {
          setPasswordConfirmError('authForm.confirmPasswordError');
        }
        break;
      default:
        break;
    }
  };

  const renderButtonText = () => {
    return state === STATE_LOGIN
      ? translate('authForm.loginButton')
      : translate('authForm.resetPasswordButton');
  };

  const renderFormHeader = (title, subtitle) => (
    <AuthFormHeader>
      <AuthFormTitle component="h1">{title}</AuthFormTitle>
      {subtitle ? <AuthFormSubtitle>{subtitle}</AuthFormSubtitle> : null}
    </AuthFormHeader>
  );

  const renderForm = () => {
    switch (state) {
      case STATE_PASSWORD_CHANGE:
        return (
          <AuthContainer>
            {renderFormHeader(translate('authForm.selectNewPassword'))}
            <AuthFormGroup>
              <AuthFieldControl fullWidth>
                <TextField
                  label={translate('authForm.fieldLabels.newPassword')}
                  id="new-password"
                  {...newPasswordInputProps}
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                    setPasswordError('');
                  }}
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => {
                    setPasswordFocused(false);
                  }}
                  type={showNewPassword ? 'text' : 'password'}
                  error={!!passwordError}
                  helperText={renderError(passwordError)}
                  slotProps={{
                    input: passwordVisibilityAdornment(showNewPassword, setShowNewPassword),
                  }}
                />
                <AuthPasswordConditions>
                  <PasswordConditionsList conditions={conditions} isFocused={isPasswordFocused} />
                </AuthPasswordConditions>
              </AuthFieldControl>
            </AuthFormGroup>
            <AuthFormGroup>
              <AuthFieldControl fullWidth>
                <TextField
                  label={translate('authForm.fieldLabels.confirmPassword')}
                  id="confirm-password"
                  {...confirmPasswordInputProps}
                  value={passwordConfirm}
                  onChange={(event) => {
                    setPasswordConfirm(event.target.value);
                    setPasswordConfirmError('');
                  }}
                  type={showConfirmPassword ? 'text' : 'password'}
                  error={!!passwordConfirmError}
                  helperText={renderError(passwordConfirmError)}
                  slotProps={{
                    input: passwordVisibilityAdornment(showConfirmPassword, setShowConfirmPassword),
                  }}
                />
              </AuthFieldControl>
            </AuthFormGroup>
          </AuthContainer>
        );
      case STATE_FORGOT_PASSWORD_CHANGE:
        return (
          <>
            {renderFormHeader(
              translate('authForm.selectNewPassword'),
              translate('authForm.verificationCodeSent'),
            )}
            <AuthFormGroup>
              <TextField
                label={translate('authForm.fieldLabels.verificationCode')}
                id="verification-code"
                {...verificationCodeInputProps}
                value={verificationCode}
                onChange={(event) => {
                  setVerificationCode(event.target.value);
                  setVerificationCodeError('');
                }}
                error={!!verificationCodeError}
                helperText={renderError(verificationCodeError)}
              />
            </AuthFormGroup>
            <AuthFormGroup>
              <AuthFieldControl fullWidth>
                <TextField
                  label={translate('authForm.fieldLabels.newPassword')}
                  id="new-password"
                  {...newPasswordInputProps}
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                    setNewPasswordError('');
                  }}
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => {
                    setPasswordFocused(false);
                  }}
                  type={showNewPassword ? 'text' : 'password'}
                  error={!!newPasswordError}
                  helperText={renderError(newPasswordError)}
                  slotProps={{
                    input: passwordVisibilityAdornment(showNewPassword, setShowNewPassword),
                  }}
                />
                <AuthPasswordConditions>
                  <PasswordConditionsList conditions={conditions} isFocused={isPasswordFocused} />
                </AuthPasswordConditions>
              </AuthFieldControl>
            </AuthFormGroup>
            <AuthFormGroup>
              <AuthFieldControl fullWidth>
                <TextField
                  label={translate('authForm.fieldLabels.confirmPassword')}
                  id="confirm-password"
                  {...confirmPasswordInputProps}
                  value={passwordConfirm}
                  onChange={(event) => {
                    setPasswordConfirm(event.target.value);
                    setPasswordConfirmError('');
                  }}
                  error={!!passwordConfirmError}
                  helperText={renderError(passwordConfirmError)}
                  type={showConfirmPassword ? 'text' : 'password'}
                  slotProps={{
                    input: passwordVisibilityAdornment(showConfirmPassword, setShowConfirmPassword),
                  }}
                />
              </AuthFieldControl>
            </AuthFormGroup>
          </>
        );
      case STATE_FORGOT_PASSWORD:
        return (
          <>
            {renderFormHeader(
              translate('authForm.forgotPassword'),
              translate('authForm.forgotPasswordMessage'),
            )}

            <AuthFormGroup>
              <TextField
                label={translate('authForm.fieldLabels.email')}
                {...usernameInputProps}
                value={username}
                onChange={(event) => {
                  setUsername(event.target.value);
                  setUsernameError('');
                  setErrorMessage('');
                }}
                id="email"
                error={!!usernameError}
                helperText={renderError(usernameError)}
              />
            </AuthFormGroup>
          </>
        );
      case STATE_LOGIN:
      default:
        return (
          <>
            {renderFormHeader(translate('authForm.loginTitle'))}
            <AuthFormGroup>
              <TextField
                label={translate('authForm.fieldLabels.email')}
                id="email"
                {...usernameInputProps}
                value={username}
                onChange={(event) => {
                  setUsername(event.target.value);
                  setUsernameError('');
                  setErrorMessage('');
                }}
                error={!!usernameError}
                helperText={renderError(usernameError)}
              />
            </AuthFormGroup>
            <AuthFormGroup>
              <TextField
                label={translate('authForm.fieldLabels.passwordLabel')}
                id="password"
                {...passwordInputProps}
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  setPasswordError('');
                  setErrorMessage('');
                }}
                type={showPassword ? 'text' : 'password'}
                error={!!passwordError}
                helperText={renderError(passwordError)}
                slotProps={{
                  input: passwordVisibilityAdornment(showPassword, setShowPassword),
                }}
              />
            </AuthFormGroup>
            <AuthForgotPasswordLinkContainer>
              <AuthNavigationLink
                component="button"
                type="button"
                color="primary"
                underline="hover"
                textAlign="right"
                disabled={isSubmitting}
                onClick={() => switchState(STATE_FORGOT_PASSWORD)}
              >
                {translate('authForm.forgotPassword')}
              </AuthNavigationLink>
            </AuthForgotPasswordLinkContainer>
          </>
        );
    }
  };

  return (
    <AuthContainer>
      {state === STATE_FORGOT_PASSWORD || state === STATE_FORGOT_PASSWORD_CHANGE ? (
        <AuthTopControls>
          <AuthCloseButton
            disabled={isSubmitting}
            onClick={() => {
              flushPasswords();
              setVerificationCode('');
              switchState(STATE_LOGIN);
            }}
            aria-label={translate('authForm.accessibility.close')}
            size="small"
          >
            <StyledCloseIcon />
          </AuthCloseButton>
        </AuthTopControls>
      ) : null}
      <AuthFormControl component="form" noValidate onSubmit={handleSubmit}>
        <AuthFormContent>{renderForm()}</AuthFormContent>
        {errorMessage ? (
          <AuthErrorAlert severity="error" onClose={() => setErrorMessage('')}>
            {renderError(errorMessage)}
          </AuthErrorAlert>
        ) : null}
        <AuthSubmitButton
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          color="primary"
          disabled={isSubmitting}
          startIcon={
            isSubmitting ? <CircularProgress size={18} thickness={5} color="inherit" /> : null
          }
        >
          {renderButtonText()}
        </AuthSubmitButton>
      </AuthFormControl>
    </AuthContainer>
  );
};

export const STATE_LOGIN = 'LOGIN';
export const STATE_FORGOT_PASSWORD = 'FORGOT_PASSWORD';
export const STATE_FORGOT_PASSWORD_CHANGE = 'FORGOT_PASSWORD_CHANGE';
export const STATE_PASSWORD_CHANGE = 'PASSWORD_CHANGE';

export default AuthForm;
